import { ConnectorConfigType, PluginConnector, PluginDataKeyMap } from './PluginConnector'
import { Subscription } from 'Core/Subscription'
import { Studio } from 'Core/Studio'

export enum ConnectorEventType {
    CONNECTOR_ADDED = 'CONNECTOR_ADDED',
    CONNECTOR_REMOVED = 'CONNECTOR_REMOVED',
}

export type ConnectorEventDataTypes = {
    [ConnectorEventType.CONNECTOR_ADDED]: {
        addedConnector: PluginConnector
    }
    [ConnectorEventType.CONNECTOR_REMOVED]: {
        removedConnector: PluginConnector
    }
}

export class PluginBlockCoordinator {
    private pluginConnectorMap: Map<string, Map<string, PluginConnector>> = new Map()
    private subscription = new Subscription<ConnectorEventType, ConnectorEventDataTypes>()
    constructor(
        private studio: Studio,
        pluginConnectorConfigs: ConnectorConfigType[]
    ) {
        pluginConnectorConfigs.forEach(config => {
            const pluginConnector = new PluginConnector(config, this.studio.blockEngine)
            const pluginConnectors = this.pluginConnectorMap.get(config.pluginId)
            if (pluginConnectors) {
                pluginConnectors.set(config.id, pluginConnector)
            } else {
                this.pluginConnectorMap.set(
                    config.pluginId,
                    new Map([[config.id, pluginConnector]])
                )
            }
        })
    }

    subscribe<T extends ConnectorEventType>(
        eventType: T,
        callback: (data: ConnectorEventDataTypes[T]) => void
    ) {
        this.subscription.subscribe(eventType, callback)
    }

    unsubscribe(eventType: ConnectorEventType) {
        this.subscription.unsubscribe(eventType)
    }

    addPluginConnector<TPluginId extends keyof PluginDataKeyMap>(
        config: ConnectorConfigType<TPluginId>
    ) {
        const pluginConnector = new PluginConnector(config, this.studio.blockEngine)
        const pluginConnectors = this.pluginConnectorMap.get(config.pluginId)
        if (pluginConnectors) {
            pluginConnectors.set(config.id, pluginConnector)
            this.subscription.publishUpdate(ConnectorEventType.CONNECTOR_ADDED, {
                addedConnector: pluginConnector,
            })
        }
    }

    removePluginConnector<TPluginId extends keyof PluginDataKeyMap>(
        config: ConnectorConfigType<TPluginId>
    ) {
        const pluginConnectors = this.pluginConnectorMap.get(config.pluginId)
        if (pluginConnectors) {
            const pluginConnector = pluginConnectors.get(config.id)
            if (pluginConnector) {
                pluginConnectors.delete(config.id)
                this.subscription.publishUpdate(ConnectorEventType.CONNECTOR_REMOVED, {
                    removedConnector: pluginConnector,
                })
            }
        }
    }

    getPluginConnectors(pluginId: string): PluginConnector[] {
        const pluginConnectors = this.pluginConnectorMap.get(pluginId)
        if (pluginConnectors) {
            return Array.from(pluginConnectors.values())
        }
        return []
    }

    getPluginConnector(pluginId: string, connectorId: string): PluginConnector | undefined {
        const pluginConnectors = this.pluginConnectorMap.get(pluginId)
        if (pluginConnectors) {
            return pluginConnectors.get(connectorId)
        }
        return
    }
}
